import React, { useMemo } from "react";
import JoditEditor from "jodit-react";
import cn from "classnames";

import styles from "./JoditWysiwyg.module.scss";

interface JoditWysiwygProps {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  hasError?: boolean;
}

const ensureTableBorders = (html: string) => {
  const document = new DOMParser().parseFromString(html, "text/html");

  document.querySelectorAll<HTMLTableCellElement>("td, th").forEach((cell) => {
    if (!cell.style.borderColor) return;

    if (!cell.style.borderStyle) {
      cell.style.borderStyle = "solid";
    }

    if (!cell.style.borderWidth) {
      cell.style.borderWidth = "1px";
    }
  });

  return document.body.innerHTML;
};

const JoditWysiwyg: React.FC<JoditWysiwygProps> = ({
  value,
  onChange,
  disabled = false,
  hasError = false,
}) => {
  const config = useMemo(
    () => ({
      theme: "dark",
      readonly: disabled,
      disabled,
      height: "calc(min(400px, 42vh) + 40px)",
      minHeight: 0,
      statusbar: false,
      allowResizeX: false,
      allowResizeY: false,
      addNewLine: false,
      addNewLineOnDBLClick: false,
      toolbarButtonSize: "small" as const,
      toolbarAdaptive: false,
      showCharsCounter: false,
      showWordsCounter: false,
      showXPathInStatusbar: false,
      buttons: [
        "font",
        "fontsize",
        "paragraph",
        "|",
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "brush",
        "|",
        "ul",
        "ol",
        "outdent",
        "indent",
        "|",
        "align",
        "link",
        "image",
        "video",
        "table",
        "|",
        "undo",
        "redo",
        "eraser",
        "source",
      ],
      table: {
        useExtraClassesOptions: false,
      },
    }),
    [disabled],
  );

  const handleBlur = (html: string) => {
    onChange(html === "<p><br></p>" ? "" : ensureTableBorders(html));
  };

  return (
    <div
      className={cn(styles.editor, {
        [styles.hasError]: hasError,
        [styles.disabled]: disabled,
      })}
    >
      <JoditEditor value={value} config={config} onBlur={handleBlur} />
    </div>
  );
};

export default JoditWysiwyg;
